"use client";

import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "@/lib/utm/hooks";
import ArticleCard from "@/app/(main)/components/ArticleCard";
import type { ArticleCardProps } from "@/app/(main)/components/ArticleCard";
import SidebarRecentPostsWidget from "@/app/(main)/components/SidebarRecentPostsWidget";
import type { Article, DestinationWithArticles } from "@/lib/articles";
import {
  formatArticleDate,
  stripHtml,
  fetchArticlesClient,
} from "@/lib/articles";
import DestinationFilterWidget from "@/app/(main)/components/DestinationFilterWidget";
import EmptyState from "@/app/(main)/components/EmptyState";
import Pagination from "@/app/(main)/components/Pagination";
import useScrollToListTop from "@/app/(main)/components/useScrollToListTop";

// ─── Props ───────────────────────────────────────────────────────────

interface ArticlesClientProps {
  initialArticles: Article[];
  initialTotalPages: number;
  initialCount: number;
  initialDestinations: DestinationWithArticles[];
  initialSidebarArticles: Article[];
  /** Destination id resolved from the URL's ?country param (empty = none). */
  initialSelectedDestId?: string;
  pageSize?: number;
  sidebarExtra?: ReactNode;
}

function toCardProps(
  article: Article,
  destinations: DestinationWithArticles[],
): ArticleCardProps {
  return {
    title: article.pageContent.title,
    photo: article.pageContent.headerPhoto || "/central.jpg",
    href: `/Article/${article.pageSEO.pageURL}`,
    date: article.createdDate,
    destinationName: article.destinationId
      ? destinations.find((d) => d.id === article.destinationId)?.nameCountry
      : undefined,
    excerpt: stripHtml(article.pageContent.description, 150),
  };
}

// ─── Component ───────────────────────────────────────────────────────

export default function ArticlesClient({
  initialArticles,
  initialTotalPages,
  initialCount,
  initialDestinations,
  initialSidebarArticles,
  initialSelectedDestId = "",
  pageSize = 6,
  sidebarExtra,
}: ArticlesClientProps) {
  const [articles, setArticles] = useState(initialArticles);
  const [sidebarArticles, setSidebarArticles] = useState(
    initialSidebarArticles,
  );
  const [destinations] = useState(initialDestinations);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(initialTotalPages);
  const [totalCount, setTotalCount] = useState(initialCount);
  const [selectedDest, setSelectedDest] = useState(initialSelectedDestId);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstRender = useRef(true);
  const scrollToListTop = useScrollToListTop(containerRef);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const loadArticles = useCallback(
    async (page: number, destId: string) => {
      setLoading(true);
      try {
        const [main, side] = await Promise.all([
          fetchArticlesClient(page, pageSize, destId || undefined),
          fetchArticlesClient(page > 1 ? page - 1 : 2, 5),
        ]);
        setArticles(main.articles);
        setTotalPages(main.totalPages);
        setTotalCount(main.count);
        setSidebarArticles(side.articles);
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadArticles(currentPage, selectedDest);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedDest]);

  // Reflect the selection in the URL's ?country param (the country name, to
  // match how destination pages link here), preserving any other params.
  function buildHref(destId: string) {
    const params = new URLSearchParams(searchParams.toString());
    const countryName = destinations.find((d) => d.id === destId)?.nameCountry;
    if (countryName) params.set("country", countryName);
    else params.delete("country");
    const qs = params.toString();
    return qs ? `${pathname}?${qs}` : pathname;
  }

  function handleDestinationChange(destId: string) {
    setCurrentPage(1);
    setSelectedDest(destId);
    router.replace(buildHref(destId), { scroll: false });
  }

  function handlePageChange(page: number) {
    setCurrentPage(page);

    // Move focus off the clicked button *before* scrolling.
    //
    // Loading disables the pagination, which force-blurs whichever button was
    // just clicked, and that focus change cancels an in-flight smooth scroll —
    // the page would twitch a couple of pixels and stop. Parking focus on the
    // listing first avoids the mid-scroll blur, and is where keyboard users
    // want to be anyway now that the content below has changed.
    containerRef.current?.focus({ preventScroll: true });

    // Scroll straight away rather than after the fetch resolves: the outgoing
    // cards are still on screen, so the target position is already correct and
    // the click feels responsive instead of stalling until the request lands.
    scrollToListTop();
  }

  // Pagination range
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  // Sidebar recent posts
  const recentPosts = sidebarArticles
    .filter((a) => !articles.some((m) => m.id === a.id))
    .slice(0, 5)
    .map((a) => ({
      id: a.id,
      title: a.pageContent.title,
      href: `/Article/${a.pageSEO.pageURL}`,
      photoURL: a.pageContent.headerPhoto || undefined,
      meta: formatArticleDate(a.createdDate),
    }));

  return (
    // tabIndex={-1} makes this a programmatic focus target only — it never
    // enters the tab order, it just receives focus on page change (see
    // handlePageChange). No focus ring: browsers only paint one for
    // :focus-visible, which scripted focus on a container doesn't trigger.
    <div ref={containerRef} tabIndex={-1}>
      <div className="row">
        {/* ── Main column ── */}
        <div className="col-xxl-8 col-lg-7">
          {/* Pagination header */}
          {totalCount > 0 && (
            <div className="d-flex align-items-center justify-content-between mb-4">
              <p className="blog-text mb-0">
                Showing{" "}
                <strong>
                  {startItem}–{endItem}
                </strong>{" "}
                of <strong>{totalCount}</strong> articles
              </p>
              {totalPages > 1 && (
                <div className="d-flex gap-2">
                  <button
                    className="th-btn style4"
                    disabled={currentPage <= 1 || loading}
                    onClick={() => handlePageChange(currentPage - 1)}
                    style={{ padding: "4px 10px" }}
                  >
                    <i className="fa fa-arrow-left"></i> Prev
                  </button>
                  <button
                    className="th-btn style4"
                    disabled={currentPage >= totalPages || loading}
                    onClick={() => handlePageChange(currentPage + 1)}
                    style={{ padding: "4px 10px" }}
                  >
                    Next <i className="fa fa-arrow-right"></i>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Article grid */}
          <div
            className="row gy-5 gx-xl-5 g-4"
            style={{ opacity: loading ? 0.5 : 1, transition: "opacity 0.2s" }}
          >
            {articles.map((article) => (
              <div key={article.id} className="col-lg-6">
                <ArticleCard {...toCardProps(article, destinations)} />
              </div>
            ))}

            {articles.length === 0 && !loading && (
              <div className="col-12">
                <EmptyState
                  icon="fa-regular fa-newspaper"
                  title="No articles found"
                  text="We couldn't find any articles matching your filter. Try a different destination or clear the filter to browse all stories."
                />
              </div>
            )}
          </div>

          {/* Bottom pagination — mirrors the press-release listing. */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            disabled={loading}
            label="Articles pagination"
          />
        </div>

        {/* ── Sidebar ── */}
        <div className="col-xxl-4 col-lg-5">
          <aside className="sidebar-area position-sticky" style={{ top: 100 }}>
            <DestinationFilterWidget
              title="Filter By Destination"
              options={destinations.map((dest) => ({
                id: dest.id,
                label: dest.nameCountry,
              }))}
              selectedId={selectedDest}
              onSelect={handleDestinationChange}
              hrefFor={(option) => buildHref(option.id)}
            />

            {/* Recent articles */}
            <SidebarRecentPostsWidget
              title="Other Travel Articles"
              items={recentPosts}
            />

            {sidebarExtra}
          </aside>
        </div>
      </div>
    </div>
  );
}
